import { Badge } from "@/components/ui";
import { HeaderText } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { BodyText } from "@/components/ui/Text";
import { ErrorBoundary } from "@/lib/monitoring";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useParams, useSearch } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { AiFeedItemCard } from "./components/AiFeedItemCard";
import { AiFeedItemStatus } from "./network/types";
import { useAiFeed } from "./network/useAiFeed";

enum AiFeedFilterTab {
  all = "all",
  needsReview = "needs-review",
  resolved = "resolved",
}

export function AiFeedContent({ compact = false }: { compact?: boolean }) {
  const { data: flagValue } = useConfigCatFeatureFlag("ehr-ai-feed-enabled");
  const [activeFilter, setActiveFilter] = useState<AiFeedFilterTab>(
    AiFeedFilterTab.all,
  );
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const {
    data: feedItems = [],
    isLoading,
    isError,
  } = useAiFeed(patientId, encounterId?.toString(), {
    enabled: Boolean(flagValue),
  });

  if (!flagValue) {
    return null;
  }

  const pendingCount = feedItems.filter(
    (item) => item.status === AiFeedItemStatus.pending,
  ).length;

  const filteredItems = feedItems.filter((item) => {
    if (activeFilter === AiFeedFilterTab.needsReview)
      return item.status === AiFeedItemStatus.pending;
    if (activeFilter === AiFeedFilterTab.resolved)
      return (
        item.status === AiFeedItemStatus.accepted ||
        item.status === AiFeedItemStatus.rejected ||
        item.status === AiFeedItemStatus.expired
      );
    return true;
  });

  const tabs: { key: AiFeedFilterTab; label: string }[] = [
    { key: AiFeedFilterTab.all, label: "All" },
    {
      key: AiFeedFilterTab.needsReview,
      label: `Needs Review${pendingCount > 0 ? ` (${pendingCount})` : ""}`,
    },
    { key: AiFeedFilterTab.resolved, label: "Resolved" },
  ];

  const encounterIdStr = encounterId?.toString();

  return (
    <div className="flex flex-col h-full">
      {!compact && (
        <div className="flex items-center gap-2 pb-3 border-b">
          <HeaderText size="md">AI Activity Feed</HeaderText>
          {pendingCount > 0 && (
            <Badge variant="info">
              <BodyText size="sm">{pendingCount}</BodyText>
            </Badge>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 py-2">
        <div className="flex items-center gap-1 flex-1">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              variant={activeFilter === tab.key ? "primary" : "ghost"}
              size="sm"
              onClick={() => setActiveFilter(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto pr-2 pt-1">
        <ErrorBoundary
          fallback={
            <div className="flex flex-col items-center justify-center py-8">
              <BodyText size="sm" textColor="error">
                Something went wrong loading the feed.
              </BodyText>
            </div>
          }
        >
          <div className="space-y-2">
            {filteredItems.map((item) => (
              <div key={item.aiFeedItemId}>
                <AiFeedItemCard
                  item={item}
                  encounterId={encounterIdStr ?? ""}
                />
              </div>
            ))}
          </div>
        </ErrorBoundary>
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8 text-tertiary">
            <Sparkles className="h-8 w-8 mb-2 animate-pulse" />
            <BodyText size="sm" textColor="tertiary">
              Loading AI activity…
            </BodyText>
          </div>
        )}
        {isError && (
          <div className="flex flex-col items-center justify-center py-8">
            <BodyText size="sm" textColor="error">
              Couldn't load the AI feed.
            </BodyText>
          </div>
        )}
        {!isLoading && !isError && filteredItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-tertiary">
            <Sparkles className="h-8 w-8 mb-2" />
            <BodyText size="sm" textColor="tertiary">
              No items to show
            </BodyText>
          </div>
        )}
      </div>
    </div>
  );
}
